import moment from "moment";
import {
    RenderCampus,
    RenderLearningMethod,
} from "./CommonGroupingItem";

/**
 * Orders filter options by label, as the legacy Tablefilter did (Intake runs newest first).
 * @param {Array<{value, label}>} options Filter options.
 * @param {boolean} [descending] Sort Z-A instead of A-Z.
 */
export function sortByLabel(options, descending = false) {
    const sorted = [...options].sort((first, second) => (
        String(first.label).trim().localeCompare(String(second.label).trim(), undefined, { numeric: true })
    ));

    return descending ? sorted.reverse() : sorted;
}

/**
 * Normalises a dropdown-options source (primitives or objects) to `{ value, label, ... }` entries.
 * @param {Array} [values] Primitives or objects to normalise.
 * @returns {Array<{value: any, label: string}>}
 */
export function normaliseOptions(values = []) {
    return (values || [])
        .map((item, index) => {
            if (item === null || item === undefined) {
                return null;
            }

            if (typeof item !== "object") {
                return {
                    value: item,
                    label: String(item),
                };
            }

            const value =
                item.value ??
                item.id ??
                item.Id ??
                item.key ??
                item.code ??
                item.name ??
                index;

            const label =
                item.label ??
                item.name ??
                item.text ??
                item.displayname ??
                item.title ??
                String(value);

            return {
                ...item,
                value,
                label,
            };
        })
        .filter(Boolean);
}

/**
 * Renders a campus id/code to its display text, falling back to the raw value.
 * @param {string|number} campus Campus id/code to render.
 * @returns {string}
 */
export function getCampusText(campus) {
    if (!campus) {
        return "-";
    }

    try {
        const rendered = RenderCampus(campus);
        return rendered?.text ? rendered.text : String(campus);
    } catch (error) {
        console.log("LinkedProgrammeUtils.js->getCampusText->Error", error);
        return String(campus);
    }
}

/**
 * Renders a learning-method id/code to its display text, falling back to the raw value.
 * @param {string|number} method Learning-method id/code to render.
 * @returns {string}
 */
export function getMethodText(method) {
    if (!method) {
        return "-";
    }

    try {
        const rendered = RenderLearningMethod(method);
        return rendered?.text ? rendered.text : String(method);
    } catch (error) {
        console.log("LinkedProgrammeUtils.js->getMethodText->Error", error);
        return String(method);
    }
}

/**
 * Picks an icon for a rendered learning-method label.
 * @param {string} methodText Rendered learning-method label (e.g. from getMethodText).
 * @returns {string}
 */
export function getMethodIcon(methodText) {
    const text = String(methodText || "").toLowerCase();

    if (text.includes("part time")) {
        return "clock";
    }
    if (text.includes("online")) {
        return "laptop";
    }
    if (text.includes("hybrid")) {
        return "university";
    }

    return "university";
}

/**
 * Formats a row's grade-lock audit info (`by <name> · <date>`), tolerating several field-name shapes.
 * @param {object} row Linked-programme row carrying grade-lock audit fields.
 * @returns {string}
 */
export function getGradeMeta(row) {
    const person =
        row?.gradeLockAudit?.user_name ||
        row?.grade_locked_by_name ||
        row?.gradeLockedByName ||
        row?.locked_by_name ||
        row?.lockedByName;

    const timestamp =
        row?.gradeLockAudit?.created_at ||
        row?.grade_locked_at ||
        row?.gradeLockedAt ||
        row?.locked_at ||
        row?.lockedAt;

    let formattedDate = "";

    if (timestamp) {
        const numericTimestamp = Number(timestamp);
        /*
         * A Unix-seconds epoch falls in this range for any date between 2001 and
         * 5138; anything outside it is treated as an already-parseable date
         * string/ms-timestamp instead of being misread as seconds.
         */
        const parsed =
            numericTimestamp > 1000000000 && numericTimestamp < 100000000000
                ? moment.unix(numericTimestamp)
                : moment(timestamp);

        if (parsed.isValid()) {
            formattedDate = parsed.format("DD MMM YY, HH:mm");
        }
    }

    if (person && formattedDate) {
        return `by ${person} · ${formattedDate}`;
    }
    if (person) {
        return `by ${person}`;
    }
    if (formattedDate) {
        return formattedDate;
    }

    return "";
}

/**
 * Whether a linked programme's grades follow the PMC's current active version (vs. a pinned override).
 * @param {object} row Linked-programme row carrying version-active/override fields.
 * @returns {boolean}
 */
export function isVersionActive(row) {
    // override_pmc_version is 0/1: 0 means this row follows the PMC's active version
    return !row?.override_pmc_version;
}

// Matchstatus codes from ready to furthest from ready (1 ready, 4/3 matched but unassigned, 2 mismatch, 0 no course)
const MATCH_STATUS_CLOSENESS = [1, 4, 3, 2, 0];

/**
 * Summarises a linked programme's module readiness from its Matchstatus-tagged
 * module list: ready/problem counts and which warning banners to show.
 * @param {object} row Linked-programme row carrying the module list.
 * @returns {{total: number, readyCount: number, problems: Array, ready: Array, warnings: Array<string>}}
 */
export function getReadiness(row) {
    const versionModules =
        row?.versionData?.moduleList ||
        row?.moduleList ||
        row?.modules ||
        [];

    /*
     * A programme with no version has no study-path modules to report on, but it
     * can still have courses linked directly (row.getSubjects, populated regardless
     * of version). Review feedback: those directly-linked courses ARE the modules
     * in that case, not a flat 0/0 — each is "ready" since there's no version/study-
     * path concept to validate it against without a version.
     */
    const sourceModules =
        versionModules.length > 0
            ? versionModules
            : (row?.getSubjects || []).map((subject) => ({
                Portal: subject?.course_name,
                Code: subject?.course_id,
                Matchstatus: 1,
                linked_course: [{ Matchstatus: 1, netsuiteProductId: subject?.netsuiteProductId }],
            }));

    const getMatchStatusRank = (course) => {
        const rank = MATCH_STATUS_CLOSENESS.indexOf(Number(course?.Matchstatus));
        return rank === -1 ? MATCH_STATUS_CLOSENESS.length : rank;
    };

    const getMismatchCount = (course) =>
        [course?.mismatchIntake, course?.mismatchCampus, course?.mismatchType].filter(Boolean)
            .length;

    const items = sourceModules.map((module) => {
        const linkedCourses = Array.isArray(module?.linked_course)
            ? module.linked_course
            : [];

        /*
         * linked_course holds every offering of this NetSuite id (all intakes,
         * campuses and types), so the module is ready if any one offering matches
         * this programme; otherwise report the offering closest to matching.
         */
        const linkedCourse = [...linkedCourses].sort(
            (first, second) =>
                getMatchStatusRank(first) - getMatchStatusRank(second) ||
                getMismatchCount(first) - getMismatchCount(second)
        )[0];

        const linkedStatus = Number(linkedCourse?.Matchstatus);

        /*
         * Default to 0 ("no course at all"), not 1 ("ready") - a module with no
         * linked course and no top-level Matchstatus has nothing making it ready.
         */
        const matchStatus = Number.isFinite(linkedStatus)
            ? linkedStatus
            : Number(module?.Matchstatus ?? 0);

        return {
            matchStatus,
            moduleName:
                module?.Portal ||
                module?.name ||
                module?.module_name ||
                "Module",
            moduleCode:
                module?.Code ||
                module?.code ||
                module?.module_code ||
                "",
            linkedCode:
                linkedCourse?.netsuiteProductId ||
                linkedCourse?.code ||
                linkedCourse?.course_code ||
                "",
            explanation:
                linkedCourse?.reason ||
                linkedCourse?.message ||
                module?.reason ||
                module?.message ||
                "",
            mismatchIntake:
                linkedCourse?.mismatchIntake ||
                linkedCourse?.intakeMismatch ||
                module?.mismatchIntake ||
                module?.intakeMismatch,
            mismatchCampus:
                linkedCourse?.mismatchCampus ||
                linkedCourse?.campusMismatch ||
                module?.mismatchCampus ||
                module?.campusMismatch,
            mismatchType:
                linkedCourse?.mismatchType ||
                linkedCourse?.typeMismatch ||
                module?.mismatchType ||
                module?.typeMismatch,
        };
    });

    const problems = items.filter((item) => item.matchStatus !== 1);
    const ready = items.filter((item) => item.matchStatus === 1);

    const total = Number(
        row?.module_total ??
            row?.moduleTotal ??
            items.length
    );

    const readyCount = Number(
        row?.module_ready ??
            row?.moduleReady ??
            Math.max(0, total - problems.length)
    );


    const MATCH_STATUS_WARNING_LABELS = {
        0: "Missing Courses",
        2: "Marking Incomplete",
        3: "Unassigned Modules",
        4: "Study Path Incomplete",
    };

    const warnings = [
        ...new Set(
            problems
                .map((item) => MATCH_STATUS_WARNING_LABELS[item.matchStatus])
                .filter(Boolean)
        ),
    ];

    return {
        total,
        readyCount,
        problems,
        ready,
        warnings,
    };
}

/**
 * Progress-bar label shown while a bulk action of the given type is running.
 * @param {"lock"|"unlock"|"setVersion"|"unlink"|string} type Bulk-action type.
 * @returns {string}
 */
export function getProgressLabel(type) {
    switch (type) {
        case "lock":
            return "Locking Grades";
        case "unlock":
            return "Unlocking Grades";
        case "setVersion":
            return "Updating Versions";
        case "unlink":
            return "Unlinking Programmes";
        default:
            return "Updating Programmes";
    }
}

/**
 * Title/description/label/icon/tone copy for a bulk-action confirmation modal.
 * @param {"lock"|"unlock"|"unlink"|string} type Bulk-action type.
 * @param {number} count Number of selected linked programmes.
 * @param {string} setVersionValue Chosen version-select value (only used for the "set version" copy).
 * @returns {{title: string, description: string, label: string, icon?: string, tone: string}}
 */
export function getActionCopy({
    type,
    count,
    setVersionValue,
}) {
    const programmeText = `${count} linked programme${
    count === 1 ? "" : "s"
  }`;

    if (type === "lock") {
        return {
            title: `Lock grades on all ${programmeText}?`,
            description:
                "All grades will become read-only for all modules and assessments linked to these programmes.",
            label: "Lock Grades",
            icon: "lock",
            tone: "primary",
        };
    }

    if (type === "unlock") {
        return {
            title: `Unlock grades on all ${programmeText}?`,
            description:
                "Grades will become editable again for all modules and assessments linked to these programmes.",
            label: "Unlock Grades",
            icon: "unlock",
            tone: "primary",
        };
    }

    if (type === "unlink") {
        return {
            title: `Unlink ${programmeText}?`,
            description:
                "The selected programmes will no longer be linked to this PMC programme.",
            label: "Unlink Programme",
            icon: "unlink",
            tone: "primary",
        };
    }

    return {
        title: `Update version on all ${programmeText}?`,
        description:
            setVersionValue === "active"
                ? "All programmes will always use the active version"
                : "All selected programmes will use the chosen version.",
        label: "Confirm",
        tone: "primary",
    };
}

/**
 * Version-select options for the bulk "set version" action, plus a leading "always active" pseudo-option.
 * @param {Array<object>} [versionFilters] Version filter options to append.
 * @returns {Array<object>}
 */
export function buildVersionOptions(versionFilters = []) {
    return [
        {
            value: "active",
            label: "Always Use Active Version",
            icon: "lock",
            iconClass: "fas",
            iconTone: "success",
        },
        ...versionFilters.map((option) => {
            const active =
                option.active ||
                option.isActive ||
                option.is_active;

            return {
                ...option,
                icon: active ? "check-circle" : "times-circle",
                iconClass: "fas",
                iconTone: active ? "success" : "muted",
                emphasis: Boolean(active),
            };
        }),
    ];
}

